import type { FC } from "react";
import { useEffect, useState } from "react";

import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { MiniFlexTable } from "@similarweb/ui-components";

import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { ChangePercentage } from "app/components/React/Table/cells";
import { TrafficShare } from "app/components/React/Table/cells/TrafficShare";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { FooterComponent } from "app/components/widget/WidgetFooter";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import { i18nFilter } from "app/filters/i18nFilter";
import { EmptyState } from "app/pages/website-analysis/traffic-sources/display-ads/common/EmptyState";
import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { CenteredFlexRow, FlexRow, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { useCurrentStateAndParams } from "@uirouter/react";

const ContentWrapper = styled(CenteredFlexRow)`
    height: 284px;
`;

const HeaderStyle = styled(FlexRow)`
    justify-content: space-between;
    box-sizing: border-box;
    padding: 24px;
`;

const CtaWrapper = styled(RightFlexRow)`
    height: 52px;
    border-top: 1px solid ${colorsPalettes.carbon[100]};

    button {
        margin: 5px 8px;
    }
`;

const CenteredCell = styled.div`
    height: 100%;
    align-items: center;
    display: flex;
`;

const ChangePercentageWrapper = styled(CenteredCell)`
    justify-content: end;
`;

const TopPublishersWrapper = styled.div<{ isCompare?: boolean }>`
    background-color: ${colorsPalettes.carbon[0]};
    height: 384px;
    box-shadow: 0 3px 6px ${rgba(colorsPalettes.carbon[500], 0.08)};
    border-radius: 4px;
    ${({ isCompare = false }) => isCompare && `margin-bottom:24px;`};
`;

const StyledTable: any = styled(MiniFlexTable)`
    &.MiniFlexTable {
        padding-bottom: 0;
        height: 232px;
        flex: 1;
    }

    .MiniFlexTable-cell {
        height: 40px;
        vertical-align: middle;
        font-size: 14px;
        &:not(:first-child) {
            border-top: 1px solid ${colorsPalettes.carbon[50]};
        }
    }

    .MiniFlexTable-column {
        &:first-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-left: 24px;
            }
        }

        &:last-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-right: 24px;
            }
        }

        &:nth-of-type(2) {
            .MiniFlexTable-cell {
                > div {
                    height: 100%;
                    align-items: center;
                    display: flex;
                }
            }
        }
    }
    .MiniFlexTable-headerCell {
        margin-bottom: 2px;
        font-weight: 500;
        font-size: 12px;
        color: ${rgba(colorsPalettes.carbon[500], 0.6)};
    }
`;

interface IWWOTopLinkDestinationsTableProps {
    unlockHook?: any;
    getCtaLink: (innerLevelPackage?: string) => {
        href: string;
        target: string;
    };
}

export const WWOTopLinkDestinationsTable: FC<IWWOTopLinkDestinationsTableProps> = ({
    getCtaLink,
    unlockHook = null,
}) => {
    const { state, params } = useCurrentStateAndParams();
    const isCompare = params.key.split(",").length > 1;
    const appMode = isCompare ? "compare" : "single";
    const { country, key, isWWW } = params;
    let { duration } = params;
    const { totalApiController, isWWWithoutPermissions } = websiteWorldWideOverviewLogic(country);
    if (isWWWithoutPermissions) {
        duration = BasicDurations.LAST_THREE_MONTHS;
    }
    const configId = proNavigator.getConfigId(state);
    const durationObject = DurationService.getDurationData(duration, undefined, configId);
    const { from, to, isWindow } = durationObject.forAPI;
    const getDateRange = (durationObject) => [durationObject.raw.from, durationObject.raw.to];
    const [fromDate, toDate] = getDateRange(durationObject);
    const i18n = i18nFilter();
    const title = i18n("wa.wwo.trafficdest.referrals.title");
    const tooltip = i18n("wa.wwo.trafficdest.referrals.title.tooltip");
    const [data, setData] = useState<object[]>(null);
    const [columns, setColumns] = useState<object[]>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const is28d = duration === "28d";
    const { selectorHandlers } = useSelectorTrigger("wwo-top-link-destinations");
    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from: fromDate.valueOf(),
                to: toDate.valueOf(),
            },
        },
        {
            filter: "country",
            countryCode: country,
            value: CountryService.getCountryById(country)?.text,
        },
        {
            filter: "webSource",
            value: "Total",
        },
    ];
    const ctaLink = getCtaLink();

    const transformData = (data) => {
        const state = proNavigator.getState(proNavigator.current());
        return data.Data.map((record) => {
            return {
                ...record,
                url: proNavigator.href(state, {
                    ...params,
                    key: record.Domain,
                }),
            };
        });
    };

    useEffect(() => {
        async function fetchData() {
            setData(null);

            const endpoint = `widgetApi/${totalApiController}/TrafficDestinationReferrals/Table`;

            try {
                const response = await DefaultFetchService.getInstance().get<{
                    Data: object[];
                    TotalCount: number;
                }>(endpoint, {
                    appMode,
                    country,
                    from,
                    includeSubDomains: isWWW === "*",
                    isWindow,
                    keys: key,
                    timeGranularity: is28d ? "Daily" : "Monthly",
                    to,
                    pageSize: 5,
                    webSource: devicesTypes.TOTAL,
                    orderBy: "TotalShare desc",
                });
                setData(transformData(response));
                setColumns(getTableColumns());
            } catch (error) {
                const err = error as Error & { status?: number };
                // 404 is the blocked or low-traffic domain response, rendered as an empty state
                if (err?.status !== 404) {
                    Sentry.errorTracker({
                        e: err,
                        message: "WWOTopLinkDestinationsTable fetchData failed",
                        level: "error",
                        params: {
                            extra: { key, country, endpoint },
                        },
                    });
                }
                setData([]);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, [from, to, isWindow, country, key]);

    const getTableColumns = () => {
        return [
            {
                field: "Domain",
                displayName: i18n("wa.wwo.toplinkdestinations.domain"),
                headerComponent: DefaultCellHeader,
                cellComponent: ({ value, row }) => {
                    const props = {
                        displayName: "CoreWebsiteCell",
                        domain: value,
                        icon: row.Favicon,
                        internalLink: row.url,
                        hideTrackButton: true,
                    };
                    return <CoreWebsiteCell {...props} />;
                },
            },
            {
                field: "Share",
                displayName: i18n("wa.wwo.toplinkdestinations.share"),
                headerComponent: DefaultCellHeader,
                cellComponent: TrafficShare,
            },
            {
                field: "Change",
                displayName: i18nFilter()("wa.wwo.toplinkdestinations.change"),
                type: "double",
                format: "percentagesign",
                cellComponent: (props) => (
                    <ChangePercentageWrapper>
                        <ChangePercentage {...props} />
                    </ChangePercentageWrapper>
                ),
                headerComponent: DefaultCellHeaderRightAlign,
                minWidth: 85,
                visible: true,
            },
        ];
    };

    const ctaClicked = () => {
        allTrackers.trackEvent("Internal Link", "click", "Traffic Destination/Outgoing Links/See more outgoing links");
    };

    const getComponent = () =>
        data && data.length > 0 && columns ? (
            <>
                <StyledTable
                    className="MiniFlexTable"
                    data={data}
                    columns={columns}
                />
                <CtaWrapper>
                    <FooterComponent
                        href={ctaLink.href}
                        onClick={ctaClicked}
                        text="wwo.trafficdest.referrals.button"
                        unlockHook={unlockHook}
                        target={ctaLink.target}
                    />
                </CtaWrapper>
            </>
        ) : (
            <ContentWrapper>
                <EmptyState
                    titleKey="wa.wwo.no.data.title"
                    subTitleKey="wa.wwo.no.data.sub.title"
                />
            </ContentWrapper>
        );

    return (
        <TopPublishersWrapper
            isCompare={isCompare}
            data-automation="wwo-top-link-destinations"
        >
            <HeaderStyle>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={subTitleFilters}
                    {...selectorHandlers}
                />
            </HeaderStyle>
            {isLoading ? (
                <ContentWrapper>
                    <WidgetLoader />
                </ContentWrapper>
            ) : (
                getComponent()
            )}
        </TopPublishersWrapper>
    );
};
